import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Building2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { restaurantsAPI } from '../lib/api';
import { RestaurantForm, restaurantToForm, formToPayload } from './RestaurantForm';
import RestaurantPlacementsByCycle from './RestaurantPlacementsByCycle';
import ContractsPanel from './ContractsPanel';
import IndustryLetterPanel from './IndustryLetterPanel';

/**
 * Right-hand drawer for one restaurant. One home for everything
 * restaurant-scoped: Details (edit form), Placements grouped by cycle,
 * Contracts (ETA) and Industry Letters.
 */
export const RestaurantDrawer = ({ restaurant, open, onOpenChange, initialTab = 'details', focusCycleId, canWrite = true, onUpdated }) => {
  const [tab, setTab] = useState(initialTab);
  const [formData, setFormData] = useState(restaurant ? restaurantToForm(restaurant) : null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  useEffect(() => {
    if (restaurant) setFormData(restaurantToForm(restaurant));
  }, [restaurant]);

  if (!restaurant) return null;

  const liveCount = (restaurant.cycles || []).reduce((n, c) => n + (c.status === 'active' ? c.filled : 0), 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await restaurantsAPI.update(restaurant.id, formToPayload(formData));
      toast.success('Restaurant updated');
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col" data-testid="restaurant-drawer">
        <SheetHeader className="px-6 pt-5 pb-0 text-left">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-ica-gold/10 rounded-lg flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-ica-gold" />
            </div>
            <div className="min-w-0 flex-1">
              <SheetTitle className="font-heading text-lg text-ica-navy leading-tight truncate" data-testid="restaurant-drawer-title">
                {restaurant.name}
              </SheetTitle>
              <SheetDescription className="text-xs text-gray-500 truncate">
                {[restaurant.cuisine_type, restaurant.contact_person, restaurant.address].filter(Boolean).join(' · ')}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
          <div className="px-6 border-b">
            <TabsList className="bg-transparent p-0 h-auto gap-4 rounded-none">
              {[
                ['details', 'Details'],
                ['placements', <>Placements {liveCount > 0 && <span className="ml-1 inline-flex items-center justify-center rounded-full bg-ica-navy text-white text-[10px] font-semibold w-5 h-5">{liveCount}</span>}</>],
                ['contracts', 'Contracts'],
                ['industry', 'Industry Letters'],
              ].map(([v, label]) => (
                <TabsTrigger
                  key={v}
                  value={v}
                  data-testid={`drawer-tab-${v}`}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-ica-gold data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-ica-navy px-1 pb-2 text-sm"
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            <TabsContent value="details" className="mt-0 space-y-4">
              {formData && <RestaurantForm formData={formData} setFormData={setFormData} disabled={!canWrite} />}
              {canWrite && (
                <div className="flex justify-end pt-2 border-t">
                  <Button data-testid="drawer-save-restaurant" onClick={handleSave} disabled={saving}>
                    <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Update'}
                  </Button>
                </div>
              )}
            </TabsContent>
            <TabsContent value="placements" className="mt-0">
              <RestaurantPlacementsByCycle restaurantId={restaurant.id} focusCycleId={focusCycleId} />
            </TabsContent>
            <TabsContent value="contracts" className="mt-0">
              <p className="text-xs text-gray-500 mb-3">Create, send, and manage Experiential Training Agreements for this Industry Partner.</p>
              <ContractsPanel mode="establishment" subjectId={restaurant.id} />
            </TabsContent>
            <TabsContent value="industry" className="mt-0">
              <p className="text-xs text-gray-500 mb-3">One confirmation letter per placement cycle, listing every student placed at this establishment.</p>
              <IndustryLetterPanel restaurant={restaurant} focusCycleId={focusCycleId} canWrite={canWrite} />
            </TabsContent>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};

export default RestaurantDrawer;
